import {CUSTOM_ELEMENTS_SCHEMA, Injector, NgModule} from '@angular/core';
import {BrowserModule} from '@angular/platform-browser';
import {AppComponent} from './app.component';
import {HTTP_INTERCEPTORS, HttpClient, HttpClientModule,} from '@angular/common/http';
import {FormsModule, ReactiveFormsModule} from '@angular/forms';
import {NgxPermissionsModule} from 'ngx-permissions';
import {TranslateLoader, TranslateModule} from '@ngx-translate/core';
import {AppRoutingModule} from './app-routing.module';
import {environment} from '../environments/environment';
import {RouterState, StoreRouterConnectingModule} from '@ngrx/router-store';
import {StoreModule} from '@ngrx/store';
import {StoreDevtoolsModule} from '@ngrx/store-devtools';
import {BrowserAnimationsModule} from '@angular/platform-browser/animations';
import {EffectsModule} from '@ngrx/effects';
import {DashboardComponent} from './features/pages/dashboard.component';
import {LoadingComponent} from './features/pages/loading.component';
import {NgbModule} from '@ng-bootstrap/ng-bootstrap';
import {ApiClientModule, OrganizationsService, SwitchLanguageService,} from '@pt/api-client';
import {
  AiAssessmentEffects,
  AppAssessmentEffects,
  AttachmentEffects,
  CovidAssessmentEffects,
  FolderEffects,
  LoadTranslations,
  PtSharedModule,
  RiskAssessmentEffects, setConfirmInjector,
  TreatmentEffects,
  WidgetEffects
} from '@pt/pt-shared';
import { CustomRouterStateSerializer, PermissionManager, PtCoreEffects, PtCoreModule } from '@pt/pt-core';
import {metaReducers, ROOT_REDUCERS} from './reducers';
import {WorkspaceEffects} from './features/home/domains/workspace/store/effects/workspace.effects';
import {ChangePasswordComponent} from './features/pages/change-password.component';
import {ForbiddenComponent} from './features/pages/forbidden.component';
import {AgGridModule} from 'ag-grid-angular';
import {DomainInterceptor} from './domain.interceptor';
import {AppNotifierEffects} from '@pt/pt-shared';
import {ToastrModule} from 'ngx-toastr';
import {CodemirrorModule} from '@ctrl/ngx-codemirror';
import localeIt from '@angular/common/locales/it';
import localeItCH from '@angular/common/locales/it-CH';
import localeEnGB from '@angular/common/locales/en-GB';
import localeFr from '@angular/common/locales/fr';
import localeFrCA from '@angular/common/locales/fr-CA';
import localeFrBE from '@angular/common/locales/fr-BE';
import localeZh from '@angular/common/locales/zh';
import {registerLocaleData} from '@angular/common';
import {AgChartsAngularModule} from 'ag-charts-angular';
import {ErrorInterceptor} from './error.interceptor';
import {UnexpectedErrorComponent} from './features/pages/unexpected-error.component';
import {setInjector} from '@gfmi/mock';
import {GenericReportFormComponent} from "./features/pages/generic-report-form.component";
import {SidebarModule} from "ng-sidebar-v2";
import { ForbiddenModuleComponent } from './features/pages/forbidden-module.component';
import { TreeModule } from '@pt/tree';
import { PtChatbotComponent } from '@pt/chatbot';
import { InputTimestampComponent } from '@pt/form';
import { OauthComponent } from './features/pages/oauth.component';
import { MfaComponent } from './features/pages/mfa.component';

registerLocaleData(localeIt);
registerLocaleData(localeItCH);
registerLocaleData(localeEnGB);
registerLocaleData(localeFr);
registerLocaleData(localeFrCA);
registerLocaleData(localeFrBE);
registerLocaleData(localeZh);

@NgModule({
  declarations: [
    AppComponent,
    DashboardComponent,
    LoadingComponent,
    ChangePasswordComponent,
    ForbiddenComponent,
    UnexpectedErrorComponent,
    ForbiddenModuleComponent,
    GenericReportFormComponent,
    OauthComponent,
    MfaComponent,
  ],
  imports: [
    BrowserModule,
    BrowserAnimationsModule,
    FormsModule,
    ReactiveFormsModule,
    HttpClientModule,
    AppRoutingModule,
    NgxPermissionsModule.forRoot(),
    ApiClientModule.forRoot({
      endPoint: environment.endPoint,
      env: environment,
      oauth: environment.oauth,
    }),
    TranslateModule.forRoot({
      loader: {
        deps: [HttpClient],
        provide: TranslateLoader,
        useFactory: LoadTranslations,
      },
      defaultLanguage: SwitchLanguageService.getDefaultLanguage(),
    }),
    SidebarModule.forRoot(),
    StoreRouterConnectingModule.forRoot({
      routerState: RouterState.Minimal,
      serializer: CustomRouterStateSerializer,
    }),
    StoreModule.forRoot(ROOT_REDUCERS, {
      metaReducers,
      runtimeChecks: {
        strictStateImmutability: true,
        strictActionImmutability: true,
        strictStateSerializability: true,
        strictActionSerializability: false, //todo restore it to true (issue with file serialization when true)
      },
    }),
    StoreDevtoolsModule.instrument({
      logOnly: environment.production,
      maxAge: 50,
    }),
    EffectsModule.forRoot([
      WorkspaceEffects,
      PtCoreEffects,
      WidgetEffects,
      TreatmentEffects,
      CovidAssessmentEffects,
      FolderEffects,
      AiAssessmentEffects,
      AppAssessmentEffects,
      AppNotifierEffects,
      RiskAssessmentEffects,
      AttachmentEffects,
    ]),
    ApiClientModule,
    PtCoreModule,
    PtSharedModule.forRoot({
      env: environment,
    }),
    NgbModule,
    AgGridModule,
    AgChartsAngularModule,
    ToastrModule.forRoot({
      tapToDismiss: true,
      disableTimeOut: false,
      preventDuplicates: true,
      positionClass: 'toast-top-center',
    }),
    CodemirrorModule,
    TreeModule,
    PtChatbotComponent,
    InputTimestampComponent,
    PtSharedModule,
  ],
  providers: [
    PermissionManager,
    OrganizationsService,
    {
      provide: HTTP_INTERCEPTORS,
      useClass: DomainInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: ErrorInterceptor,
      multi: true,
    },
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  bootstrap: [AppComponent],
})
export class AppModule {
  constructor(i: Injector) {
    setInjector(i);
    setConfirmInjector(i);
  }
}
